import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getProfileData,
  getProfileFromDatabase,
  saveProfileData,
  syncProfileToSupabase,
} from '../../services/storage/userDataStorage';
import { isSimpleTier as isSimpleTierNow } from '../../utils/userTier';
import { isSansChatVariant, getVariantQrTag } from '../../utils/exchangeVariant';
import QrPhotoSheet from '../../components/Overlay/QrPhotoSheet';
import { Lightning } from '@phosphor-icons/react/dist/ssr/Lightning';
import { generateChatRoomId } from '../../utils/chatUtils';
import {
  hasLocationPermission,
  resetLocationPermission,
} from '../../services/location/locationService';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import { useConnectionContext } from '../../contexts/ConnectionStateContext';
import { joinChatRoom, isChannelHealthy } from '../../services/chat/chatService';
// getSessionDuration removed - session reuse logic deprecated with P2P
import LocationPrompt from '../../components/Location/LocationPrompt';
import BrowserPermissionModal from '../../components/Location/BrowserPermissionModal';
import CustomQRDisplay from '../../components/QR/CustomQRDisplay';
import Container from '../../components/UISystem/Layout/Container';
import { ButtonRounded } from '../../components/UISystem/Buttons';
import { COLORS, SPACING, TYPOGRAPHY, SHADOWS } from '../../styles/UISystem';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { ArrowClockwise } from '@phosphor-icons/react/dist/ssr/ArrowClockwise';
import { Scan } from '@phosphor-icons/react/dist/ssr/Scan';
import { AnimatePresence } from 'framer-motion';
import ProximityCard from '../../components/Proximity/ProximityCard';
import useProximity from '../../hooks/useProximity';
import { IconCircleButton } from '../../components/UISystem/Icons/IconCircleButton';
// VERSION import removed - replaced with profileUserId in dev bar
// ContactSelectionBottomSheet removed - overlay now shows automatically on ProfileConnectionPage
// ProfileConnectBottomSheet removed - P2P discovery code deprecated
const QRScanner = React.lazy(() => import('../../components/Scanner/QRScanner'));
import QuickAddOverlay from '../../components/Overlay/QuickAddOverlay';
import { getLocationWithVenue, getCurrentLocation } from '../../services/location';
import { beginNearbyTiming, noteNearby } from '../../services/proximity/nearbyTiming';
import useLocationPromptComplete from '../../hooks/useLocationPromptComplete';
import {
  activateSession,
  deactivateSession,
  SESSION_FRESHNESS_MS,
  touchSession,
  confirmHostConnection,
} from '../../services/activeSession';
// nanoid import removed — quick add moved to ProfileConnectionsListPage
import {
  logConnectionError,
  // logAppOpen — moved to ProfileConnectionsListPage (app entry point)
  logConnectionLocation,
  logManualAdd,
} from '../../services/analytics/eventLogger';
import { getProfileUserId, getOrCreateProfileUserId } from '../../utils/identityUtils';
import { handleAnonDetected, setupDetectionLayers, resetDetectionGuard } from './qrDetection';
import {
  handlePXPHostExchange,
  handlePXPGuestScan,
  showScanLine,
  COULD_NOT_CONNECT,
} from './qrPXPExchange';
import { prepareHostOffer } from './pxpOffer';
import { takePendingProfileScan } from '../../utils/pendingProfileScan';
import { updateLiveLocation } from '../../services/location/liveLocationService';
import { isPWA } from '../../utils/installPrompt';
import { isRoomStaged } from '../../services/exchange/connectSheets';
import { isNewExperience } from '../../utils/newExperience';
import GuestCodePage from '../NewExperience/GuestExchange/GuestCodePage';
import { CodeStatusRow, EditProfileIdModal } from './CodeStatusRow';
// Not main: the build number and the edit-code tool show only on test sites
import { isBranchDeploy } from '../../utils/devTools';

// The QR screen's location primer asks at most ONCE per browser session
// (device pass 2, 2026-08-08: answered primers kept re-firing across the
// screen's multiple triggers). Simple tier never asks here at all — the
// People Connect tap owns its one ask (device pass 1 ruling). A new session
// may ask again; a stored grant (localStorage) quiets it permanently.
// Simple tier ASKS here now (user ruling 2026-08-15, reversing the device-pass-1
// call that handed the one ask to the People Connect tap). Why it changed: a
// freshly minted SP is navigated straight from "Make QR" onto this screen, so
// the Connect tap that owned the ask may come much later or never — they could
// hand the phone over having been asked nothing, and the meeting lands with no
// venue. This screen is the moment location matters. The session guard below
// still holds, so nobody is asked twice.
// Quiet is earned by location WORKING, not by having been asked (fix
// 2026-08-20). The session flag is now written only on an actual grant: a
// dismissal or a denial used to write it too, so one stray tap silenced both
// ask paths for the whole browser session while `hasLocationPermission()`
// stayed false — location failed and nothing ever asked again. Compounded by
// iOS's auto-deny cooldown after a dismissed-without-choosing dialog.
//
// The re-firing this guard was added to stop (device pass 2) is still stopped:
// a decline goes quiet for the current QR open via the ref below, and a
// deliberate close-and-reopen is treated as a fresh request.
const isLocationPrimerAnswered = () =>
  sessionStorage.getItem('qr_location_primer_answered') === 'true';
const markLocationPrimerAnswered = () =>
  sessionStorage.setItem('qr_location_primer_answered', 'true');

// Sans-chat waiting window — IS the active-session staleness bound (past it,
// a scan cannot succeed server-side, so the client stops paying). Aliased,
// not copied: the touch-at-close below is only correct because these match.
const STALE_WAIT_WINDOW_MS = SESSION_FRESHNESS_MS;

/**
 * QR Screen for Profile User
 *
 * Displays a QR code for Anon Users to scan
 */
const QRScreen = ({ isOpen, onClose }) => {
  // Get navigation context
  const { navigateTo } = useNavigation();

  // Simple-tier chrome (tiered-exchange Step 5, mock 6a): photo sheet then
  // location sheet on first land (photo-less SP), "Show this to swap contacts"
  // caption, and the standard down caret as the exit.
  const isSimpleTier = isSimpleTierNow();
  // The NEW code page, every host (signup E3a Step 9 for a Simple, E3b-1 step
  // 9 for a Full host): a render swap only — detection, the session and
  // formation below are unchanged; after a scan the page raises Share and lands
  // on new People itself.
  const newCodePage = isNewExperience();
  // The scan just made — { room, nameKnown, repeat }, a new object per scan:
  // the new code page's sheet and gate are about this person
  const [scan, setScan] = useState(null);
  // A formed room is spent: the next open — even a quick one inside the 2.5 s
  // close debounce — rotates to a fresh room and re-arms detection, or a second
  // guest in the same session joins the first guest's room and is never
  // detected (device pass 2026-10-20, 5a; e2e "a second guest in the same
  // session"). The sans-chat settled close's rotate, reused.
  // The room is the first scanner's alone (Mark, 2026-10-21): the new code
  // page stays up through the share and waiting sheets, so the lock is the
  // server's claim (052 claim_qr_room), taken by the scanner before it writes
  // anything — every later phone gets "<first name>'s connecting with someone
  // else" while the code stays open. The waiting sheet's X or expiry opens a
  // fresh, unclaimed room (rearm).
  const onFormed = useCallback((room, { nameKnown = false, repeat = false } = {}) => {
    reopenRotateRef.current = true;
    setScan({ room, nameKnown, repeat });
  }, []);
  // The new code page back on its bare code while still open (the waiting
  // sheet's X or expiry): the code is scannable again (Profile spec), so the
  // spent room rotates now, not at the next open. Only the new code page calls it.
  const [rotateKey, setRotateKey] = useState(0);
  const rearm = useCallback(() => {
    reopenRotateRef.current = true;
    setRotateKey((k) => k + 1);
  }, []);
  // Two profile holders on the new code page (E3b-2a): the offer — a scan of
  // this code, this phone's own scan, or the scan a nearby match runs — and a
  // nearby match whose host turned out busy (P4). Nothing is recorded until
  // both have tapped; the page owns the rest (useCodePageOffer).
  const [offer, setOffer] = useState(null);
  const [offerFailed, setOfferFailed] = useState(null);
  const onHostOffer = useCallback((o) => {
    // The room is claimed: whatever happens, the next open rotates
    reopenRotateRef.current = true;
    setOffer(o);
  }, []);
  useEffect(() => {
    if (!isOpen) {
      setScan(null);
      setOffer(null);
    }
  }, [isOpen]);
  const [photoSheetDismissed, setPhotoSheetDismissed] = useState(
    () => sessionStorage.getItem('qr_photo_sheet_dismissed') === 'true'
  );
  // The beat before the photo sheet raises — the full QR page shows for a
  // moment first (resets on close, so a reopen gets the same beat)
  const [photoSheetBeat, setPhotoSheetBeat] = useState(false);
  useEffect(() => {
    if (!isSimpleTier || !isOpen || photoSheetDismissed) {
      setPhotoSheetBeat(false);
      return undefined;
    }
    const t = setTimeout(() => setPhotoSheetBeat(true), 1500);
    return () => clearTimeout(t);
  }, [isSimpleTier, isOpen, photoSheetDismissed]);
  const dismissPhotoSheet = () => {
    sessionStorage.setItem('qr_photo_sheet_dismissed', 'true');
    setPhotoSheetDismissed(true);
  };
  // Get session context
  const { updateSessionChatRoomId, sessionChatRoomId } = useSession();
  const { updateConnectionMetadata, updateAnonData, updateManualContactData, updateNotes } =
    useConnectionContext();
  // Set theme-color to white for status bar when overlay opens
  // iOS Safari requires aggressive toggling to notice dynamic changes
  useEffect(() => {
    if (!isOpen) return;
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      // Toggle to force iOS Safari to re-read the meta tag
      metaThemeColor.setAttribute('content', '#fefefe');
      requestAnimationFrame(() => {
        metaThemeColor.setAttribute('content', '#ffffff');
      });
    }
  }, [isOpen]);

  // Component mount/open log
  useEffect(() => {
    if (isOpen) {
      console.log('QRScreen overlay opened at:', new Date().toISOString());
    }
  }, [isOpen]);

  // State management
  const [chatRoomId, setChatRoomId] = useState(null);
  // Declined (dismissed or denied) during THIS QR open. Silences the re-ask
  // while the screen stays open, and resets on close so reopening the QR — a
  // deliberate act, and the moment location matters — asks again.
  const primerDeclinedThisOpenRef = useRef(false);
  const isLocationPrimerQuiet = () =>
    isLocationPrimerAnswered() || primerDeclinedThisOpenRef.current;
  const [qrCodeUrl, setQRCodeUrl] = useState('');
  // Read by photoSheetPending below and by onPhotoSelected (the stale
  // "unused" suppression was removed at /simplify 2026-08-17)
  const [profile, setProfile] = useState(null);
  // The photo sheet still has an ask outstanding — it owns the screen until it
  // is answered, so the location sheet waits behind it (one sheet at a time,
  // photo first: user ruling 2026-08-16).
  // No photo ask on the new code page (plan settled item 1: + Add photo lives on the card)
  const photoSheetPending =
    isSimpleTier && !newCodePage && !profile?.photoUrl && !photoSheetDismissed;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Permanent URL state for new connection mechanism
  const [profileUserId, setProfileUserId] = useState(null);
  // WebSocket connection status for indicator
  const [wsConnected, setWsConnected] = useState(false);
  // Unified readiness state - tracks all systems needed for exchange to work
  // "Ready" indicator only shows when ALL of these are true
  const [sessionActive, setSessionActive] = useState(false); // active_sessions DB row exists
  const [profileSynced, setProfileSynced] = useState(false); // profiles DB row exists
  // Derived readiness: true only when WebSocket + Session + Profile are all ready
  const isFullyReady = wsConnected && sessionActive && profileSynced;
  // Connection stuck detection - shows "Tap to refresh" after 10s without reaching ready
  const [connectionStuck, setConnectionStuck] = useState(false);
  // The new code page's location prompt comes first and alone (step 16, Mark
  // 2026-10-21; CD 5A.8 / 5A.8·b): while it's up the room isn't live yet and
  // the code is blurred. Resolved by the prompt's answer, or false on a close.
  const [awaitingLocation, setAwaitingLocation] = useState(false);
  const locationAnswerRef = useRef(null);

  // Connection stuck timer - if not fully ready after 10s, offer refresh
  // (not while the room is deliberately waiting for the location answer)
  useEffect(() => {
    if (!isOpen || isFullyReady || awaitingLocation) {
      setConnectionStuck(false);
      return;
    }
    const timer = setTimeout(() => setConnectionStuck(true), 10000);
    return () => clearTimeout(timer);
  }, [isOpen, isFullyReady, awaitingLocation]);

  // Edit Profile ID modal (dev tool, accessible from /u/ indicator)
  const [showEditProfileId, setShowEditProfileId] = useState(false);
  // Scanner mode toggle
  const [showScanner, setShowScanner] = useState(false);
  // Scanner status message (self-scan, invalid URL, etc.)
  const [scanMessage, setScanMessage] = useState(null);
  // PWA detection for URL bar display — uses shared utility from installPrompt.js
  const pwaMode = isPWA();
  // Quick-add overlay state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddChatRoomId, setQuickAddChatRoomId] = useState(null);
  const [quickAddMetadata, setQuickAddMetadata] = useState(null);

  // Contact selection bottom sheet state removed - overlay now auto-shows on ProfileConnectionPage

  // Ref to store profile data for use in WebSocket handler before state updates
  // This allows the handler to access profile data even while React state is updating
  const profileRef = useRef(null);
  // Ref to store WebSocket cleanup function for cleanup on unmount
  const wsCleanupRef = useRef(null);
  // Ref to store detection layer cleanup — always torn down on QR close
  // (separate from wsCleanupRef because WebSocket may stay alive for ProfileConnectionPage)
  const detectionCleanupRef = useRef(null);
  // Overlay lifecycle refs — track init state and debounced cleanup
  const isInitializedRef = useRef(false);
  const cleanupTimerRef = useRef(null);
  // Sans-chat: a settled close (past the 2.5s debounce) ends that exchange
  // moment — the NEXT open must mint a fresh room (B28: without rotation the
  // first scan's formed room captured every later scanner as a ghost).
  const reopenRotateRef = useRef(false);
  // Sans-chat waiting-window teardown: detection survives a settled close only
  // as long as a scan could still succeed (the 5-min session staleness) —
  // then the client stops polling (review 2026-08-09).
  const staleTeardownTimerRef = useRef(null);
  // Location refs: fetch early (QR init), save late (after connection confirmed)
  // Promise ref holds the getLocationWithVenue() Promise so handleAnonDetected can await it
  // Data ref holds the resolved result for instant access
  const locationPromiseRef = useRef(null);
  const locationDataRef = useRef(null);
  // The GPS fix alone (no place lookup) — the host's reconnect check waits on this,
  // not on the slower venue lookup (meetingDecision)
  const coordsPromiseRef = useRef(null);

  // ─── Proximity (→ useProximity hook) ────────────────────────────────────────
  // Ref bridges definition order: handleScanSuccess is defined later in the component body
  // but the hook's PXP bridge needs it. Assigned after handleScanSuccess is defined below.
  const scanSuccessRef = useRef(null);
  const {
    proximityState,
    nearbyUser,
    setProximityCoords,
    onConnect: handleProximityConnect,
    onDismiss: handleProximityDismiss,
    invite: inviteNearby,
    withdraw: withdrawNearby,
    waitingOn: nearbyWaitingOn,
    resetProximity,
    cleanupProximity,
  } = useProximity({
    profileUserId,
    // PXP contract: both users on their QR PAGES (device pass 2: a closed QR
    // kept broadcasting presence under sans-chat's survive-close, so a user
    // sitting on People read as "Nearby"). Composing isOpen in makes the
    // proximity channel structurally impossible while the overlay is closed.
    isFullyReady: isFullyReady && isOpen,
    profileRef,
    onPXPScan: (url) => scanSuccessRef.current?.(url, 'proximity'),
  });

  // A profile holder opened someone's link (App.js → pendingProfileScan):
  // scan it once this screen is ready — the same handshake as the in-app
  // scanner and Proximity (signup-connect step 3). Read once, so it runs once.
  useEffect(() => {
    if (!isOpen || !isFullyReady) return;
    const url = takePendingProfileScan();
    if (url) scanSuccessRef.current?.(url, 'link_scan');
  }, [isOpen, isFullyReady]);

  // Ref to store onClose for use in async handlers (avoids stale closures)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Handle connections button click — close overlay to reveal underlying screen
  const handleViewConnections = () => {
    console.log('[QR_SCREEN] View connections clicked, closing QR overlay');
    if (onCloseRef.current) {
      onCloseRef.current();
    }
  };

  // Location prompt completion + terminal permission modal — the shared
  // grant/deny/dismiss branching lives in useLocationPromptComplete (connect
  // context: declines are NOT logged); only the connect-flow proceed is local.
  // Location is required and KEPT for v7826 (Mark, 2026-10-05): once the
  // Connect screen has a fix, store it on the existing live-location rail —
  // kept, not shared (signup-connect step 5). The writer's 5-minute guard
  // keeps repeat opens cheap. Fire-and-forget: the exchange never waits on it.
  // Reads the id at call time (the async location callbacks can outlive the
  // render that created them).
  const keepLocation = (coords) => {
    const id = getProfileUserId();
    if (id) updateLiveLocation(id, { coords });
  };

  const {
    showLocationPrompt,
    setShowLocationPrompt,
    showPermissionModal: showBrowserPermissionModal,
    handlePromptComplete: handleLocationPromptComplete,
    dismissPermissionModal: handleDismissPermissionModal,
  } = useLocationPromptComplete({
    context: 'connecting',
    onGranted: (coords) => {
      if (!coords) return;
      console.log('QRScreen: Location permission granted, using coords from LocationPrompt');

      // Reuse coords from LocationPrompt — no second GPS call needed
      coordsPromiseRef.current = Promise.resolve(coords);
      setProximityCoords(coords);
      keepLocation(coords);
      locationPromiseRef.current = getLocationWithVenue(coords)
        .then((data) => {
          locationDataRef.current = data;
          console.log('[QR_SCREEN] Location ref populated after permission grant');
          return data;
        })
        .catch((err) => {
          console.error('[QR_SCREEN] Location fetch after permission grant failed:', err);
          return null;
        });
    },
  });

  // Store processed chat room IDs to avoid duplicating location updates
  const processedChatRoomIds = useRef(new Set());

  // Main initialization — two phases:
  // PHASE A (instant): localStorage reads → validate profile → show QR. <50ms, zero network.
  // PHASE B (background): session activation, WebSocket, sync, detection layers.
  // Uses isInitializedRef to avoid re-running if already active
  useEffect(() => {
    if (!isOpen) return;
    if (isInitializedRef.current) {
      if (reopenRotateRef.current) {
        // Reopen after a settled close (sans-chat): every QR open is a fresh
        // exchange — the legacy per-open contract. Tear down the old room's
        // detection + channel and fall through to a full re-init, which mints
        // a new room, re-snapshots the profile, and re-arms detection (B28).
        reopenRotateRef.current = false;
        console.log('[QR_SCREEN] Reopen after settled close — rotating to a fresh exchange room');
        if (detectionCleanupRef.current) {
          detectionCleanupRef.current();
          detectionCleanupRef.current = null;
        }
        if (!isRoomStaged(chatRoomId) && wsCleanupRef.current) {
          wsCleanupRef.current();
          wsCleanupRef.current = null;
          setWsConnected(false);
        }
        setSessionActive(false);
        setProfileSynced(false);
        isInitializedRef.current = false;
      } else {
        console.log('[QR_SCREEN] Already initialized, skipping');
        // Quick flick (close/reopen inside the debounce) keeps the room —
        // re-snapshot the profile so PXP/proximity payloads carry channels
        // and photos saved since the first open (B20).
        getProfileData().then((fresh) => {
          if (fresh) {
            profileRef.current = fresh;
          }
        });
        return;
      }
    }
    isInitializedRef.current = true;
    // A fresh init consumes any pending rotate flag (a stale-window teardown
    // leaves it set; without this a later quick-flick would rotate spuriously)
    reopenRotateRef.current = false;
    resetDetectionGuard(); // Reset for new QR session
    console.log('QRScreen initialize effect running');

    const initialize = async () => {
      try {
        // ========== STEP 1: Generate chatRoomId IMMEDIATELY (instant) ==========
        // Each QR screen visit = fresh exchange
        let newChatRoomId = generateChatRoomId();
        console.log('[QR_SCREEN] GENERATING NEW chatRoomId:', newChatRoomId);

        // Connection timing: profile T0 starts at anon_detected (see handleAnonDetected)

        // Update state and session immediately
        setChatRoomId(newChatRoomId);
        const shouldForceUpdate = newChatRoomId !== sessionChatRoomId;
        updateSessionChatRoomId(newChatRoomId, shouldForceUpdate);
        console.log('[QR_SCREEN] Updated session with force=' + shouldForceUpdate);

        // ========== STEP 2: Load profileUserId IMMEDIATELY (instant) ==========
        // getOrCreateProfileUserId() generates if missing, regenerates if malformed
        const currentProfileUserId = getOrCreateProfileUserId();
        setProfileUserId(currentProfileUserId);

        // ========== PHASE A STEP 3: Load profile, validate, show QR ==========
        // getProfileData reads from localStorage — resolves ~instantly
        const profileData = await getProfileData();
        console.log('[QR_SCREEN] Loaded Profile data:', profileData);

        // Validate profile has required fields
        const hasAtLeastOneContact = (profile) => {
          return !!(
            profile?.phone?.trim() ||
            profile?.email?.trim() ||
            profile?.instagram?.trim() ||
            profile?.linkedin?.trim() ||
            profile?.whatsapp?.trim()
          );
        };

        if (!profileData) {
          console.log('[QR_SCREEN] No profile data found, redirecting to profile setup');
          setIsLoading(false);
          navigateTo('profile-settings');
          if (onCloseRef.current) onCloseRef.current();
          return;
        }

        const hasValidProfile = profileData.name?.trim() && hasAtLeastOneContact(profileData);
        if (!hasValidProfile) {
          console.log('[QR_SCREEN] Profile incomplete, redirecting to profile setup');
          setIsLoading(false);
          navigateTo('profile-settings');
          if (onCloseRef.current) onCloseRef.current();
          return;
        }

        sessionStorage.setItem('user_type', 'profile');
        console.log('[QR_SCREEN] Set user_type to profile');

        // Set refs BEFORE QR display (handlers may need profile immediately)
        profileRef.current = profileData;
        setProfile(profileData);

        // Generate QR URL and show QR — INSTANT
        // QR URL only needs profileUserId (localStorage). Zero network calls.
        // Scan-carries-variant: a non-chat host's tag ('?v=c'/'?v=t') rides the
        // URL so the scanner mirrors the host's variant for that visit only
        // ('' on chat and on every main build — prod QR bytes untouched).
        const baseUrl = window.location.origin;
        const permanentUrl = `${baseUrl}/u/${currentProfileUserId}${getVariantQrTag()}`;
        console.log('[QR_SCREEN] Generated permanent QR URL:', permanentUrl);
        setQRCodeUrl(permanentUrl);

        const legacyUrl = `${baseUrl}?s=a&c=${newChatRoomId}`;
        console.log('[QR_SCREEN] Legacy URL (for reference):', legacyUrl);

        setIsLoading(false);
        console.log('[QR_SCREEN] Phase A complete — QR visible');

        // logAppOpen() — moved to ProfileConnectionsListPage (app entry point)

        // ══════════════════════════════════════════════════════════════
        // PHASE B — Background (after QR visible)
        // Network calls for session, WebSocket, sync, and detection.
        // The await below yields to the event loop, letting React render
        // the QR before continuing.
        // ══════════════════════════════════════════════════════════════

        // Define message handler (uses profileRef + currentProfileUserId from Phase A)
        // This handler uses profileRef so it can work even before profile state updates
        const handleMessage = async (message) => {
          console.log('[QR_SCREEN] Received message:', JSON.stringify(message));

          // Extract message payload from event
          let payload;
          try {
            if (message.event === 'message') {
              payload = message.payload;
            } else if (message.payload) {
              payload = message.payload;
            } else {
              payload = message;
            }
          } catch (parseError) {
            console.error('[QR_SCREEN] Error extracting message payload:', parseError);
            return;
          }

          // Handle profile_data_response messages (from Profile user during PXP)
          if (payload.type === 'profile_data_response') {
            console.log('[QR_SCREEN] Received profile_data_response from Profile user during P2P');

            // Skip self-sent messages using profileUserId identity
            if (payload.senderProfileUserId === currentProfileUserId) {
              console.log('[QR_SCREEN] ❌ Skipping self-sent profile_data_response');
              return;
            }

            // Persistence handled by global handler in ConnectionStateContext
            return;
          }

          // Handle 'contact' messages (from Anon user sharing contact info)
          if (payload.type === 'contact' && payload.sender === 'anon') {
            console.log('[QR_SCREEN] Received contact data from Anon user');

            // Persistence handled by global handler in ConnectionStateContext
            // Location delivery consolidated on metadata_updated (sent by background IIFE in handleAnonDetected)

            return;
          }

          // Skip messages from self (using profileUserId identity)
          if (payload.senderProfileUserId === currentProfileUserId) {
            console.log('[QR_SCREEN] Ignoring self-sent message');
            return;
          }

          // Handle profile_exchange messages (PXP: → qrPXPExchange.js)
          // The new code page: nothing recorded until both tap (pxpOffer, E3b-2a)
          if (payload.type === 'profile_exchange' && newCodePage) {
            const hostOffer = await prepareHostOffer(payload, {
              chatRoomId: newChatRoomId,
              profileUserId: currentProfileUserId,
              profileRef,
              locationDataRef,
              locationPromiseRef,
              coordsPromiseRef,
              updateAnonData,
              updateConnectionMetadata,
              updateSessionChatRoomId,
            });
            onHostOffer(hostOffer);
            return;
          }
          if (payload.type === 'profile_exchange') {
            await handlePXPHostExchange(payload, {
              chatRoomId: newChatRoomId,
              profileUserId: currentProfileUserId,
              profileRef,
              locationDataRef,
              locationPromiseRef,
              coordsPromiseRef,
              updateAnonData,
              updateConnectionMetadata,
              updateSessionChatRoomId,
              navigateTo,
              onClose: onCloseRef.current,
              confirmHostConnection,
            });
            return;
          }
        };

        // ========== PHASE B: Session activation (sequential), then WebSocket ==========
        // Session activation runs first so we know the correct chatRoomId before
        // joining WebSocket. activateSession guards against overwriting a session
        // where an Anon is mid-connection — if so, it returns the existing chatRoomId.
        console.log('[QR_SCREEN] Phase B: Starting background initialization...');
        const initStartTime = Date.now();

        // Tracks whether activateSession's protect-guard adopted an existing
        // room — detection uses it to scope the formed-room ghost check.
        let isRejoinedSession = false;

        // The new code page: no connection until the location prompt is
        // answered (step 16) — the same test the isOpen effect asks with, so
        // the room waits exactly when the prompt shows. Get location, Later or
        // a tap outside all answer it; a close abandons this init (the next
        // open starts fresh).
        if (newCodePage && !isLocationPrimerQuiet() && !(await hasLocationPermission())) {
          setAwaitingLocation(true);
          setShowLocationPrompt(true);
          // Close whatever room the code's link still points at (one left live
          // by an earlier visit that never closed, e.g. an app kill): nobody
          // joins anything until the answer
          await deactivateSession(currentProfileUserId);
          const answered = await new Promise((resolve) => {
            locationAnswerRef.current = resolve;
          });
          setAwaitingLocation(false);
          if (!answered) {
            isInitializedRef.current = false;
            return;
          }
        }

        try {
          const sessionResult = await activateSession(currentProfileUserId, newChatRoomId);
          if (sessionResult?.[0]) {
            const returnedChatRoomId = sessionResult[0].chat_room_id;
            if (returnedChatRoomId !== newChatRoomId) {
              // Guard fired — existing Anon connection. Switch to existing chatRoomId.
              console.log('[QR_SCREEN] Rejoining existing session:', returnedChatRoomId);
              isRejoinedSession = true;
              newChatRoomId = returnedChatRoomId;
              setChatRoomId(returnedChatRoomId);
              updateSessionChatRoomId(returnedChatRoomId, true);
            }
            setSessionActive(true);
            // (Device pass 2 ruling: no pre-scan waiting card — People shows
            // "Waiting to connect..." only in the scan→name window, driven by
            // the justFormed params. Nothing to flag here anymore.)
          }
        } catch (err) {
          console.error('[QR_SCREEN] Failed to activate session:', err);
          logConnectionError('session_activation', {
            message: err.message || 'Failed to activate session',
            code: err.code || 'unknown',
            profileUserId: currentProfileUserId,
            chatRoomId: newChatRoomId,
            profileUsername: profileRef.current?.name || null,
          });
          setSessionActive(false);
        }

        // WebSocket joins after session — uses the correct chatRoomId
        try {
          const cleanup = await joinChatRoom(newChatRoomId, handleMessage);
          wsCleanupRef.current = cleanup;
          console.log('[QR_SCREEN] WebSocket connected');
          setWsConnected(true);
        } catch (err) {
          console.error('[QR_SCREEN] WebSocket connection failed:', err);
          const profileUsername = profileRef.current?.name;
          if (profileUsername) {
            logConnectionError('websocket_profile', {
              message: err.message || 'WebSocket connection failed',
              chatRoomId: newChatRoomId,
              profileUsername,
            });
          }
          setWsConnected(false);
        }

        console.log(`[QR_SCREEN] Phase B network init complete in ${Date.now() - initStartTime}ms`);
        // phase_b_complete timing removed — happens before anon scan

        // Verify profile sync (NON-BLOCKING background IIFE)
        // Once verified, marks profileSynced=true for the Ready indicator
        // Use canonical identity from localStorage (not profileData object which may be stale)
        const canonicalProfileUserId = getProfileUserId();
        if (canonicalProfileUserId) {
          (async () => {
            try {
              console.log('[QR_SCREEN] Background: Verifying profile sync...');
              const dbProfile = await getProfileFromDatabase(canonicalProfileUserId);

              // Re-read the profile fresh — the closure's profileData snapshot can be
              // stale by the time this background check fires (defense-in-depth on top
              // of the sync's non-destructive merge).
              const freshProfile = await getProfileData();
              if (!freshProfile) {
                console.warn('[QR_SCREEN] Background: No local profile to sync, skipping');
                setProfileSynced(false);
                return;
              }

              if (!dbProfile) {
                console.log('[QR_SCREEN] Background: Profile not in database, syncing...');
                await syncProfileToSupabase(freshProfile);
                console.log('[QR_SCREEN] Background: Profile synced to database');
                setProfileSynced(true);
              } else if (freshProfile.photoUrl !== dbProfile.photo_url) {
                console.log('[QR_SCREEN] Background: Photo URL mismatch, syncing...', {
                  local: freshProfile.photoUrl?.substring(0, 50),
                  db: dbProfile.photo_url?.substring(0, 50),
                });
                await syncProfileToSupabase(freshProfile);
                console.log('[QR_SCREEN] Background: Profile photo synced');
                setProfileSynced(true);
              } else {
                console.log('[QR_SCREEN] Background: Profile sync verified - URLs match');
                setProfileSynced(true);
              }
            } catch (err) {
              console.error('[QR_SCREEN] Background sync error:', err);
              setProfileSynced(false);
            }
          })();
        }

        // NOTE: Anon detection is handled entirely by DB layers (postgres_changes, polling)
        // via qrDetection.js module. No presence listener needed here.

        // ========== Database-backed Anon detection (→ qrDetection.js) ==========
        const detectionConfig = {
          chatRoomId: newChatRoomId,
          profileUserId: currentProfileUserId,
          isRejoinedSession,
          profileRef,
          locationDataRef,
          locationPromiseRef,
          coordsPromiseRef,
          updateConnectionMetadata,
          updateSessionChatRoomId,
          navigateTo,
          onClose: onCloseRef.current,
          confirmHostConnection,
          // The new code page stays up and raises its Share sheet (E3a Step 9)
          onFormed: newCodePage ? onFormed : undefined,
        };
        const detectionCleanup = setupDetectionLayers(
          newChatRoomId,
          (source, connectedAt, returningRoomId) => {
            handleAnonDetected(source, connectedAt, detectionConfig, returningRoomId);
          }
        );

        // Store detection cleanup separately — must always run on QR close,
        // even when WebSocket is kept alive for ProfileConnectionPage.
        detectionCleanupRef.current = detectionCleanup;
      } catch (err) {
        console.error('Error initializing QR screen:', err);
        setError('Failed to initialize QR code. Please try again.');
        setIsLoading(false);
      }
    };

    initialize();
    // rotateKey: the new code page's rearm (never changes on the old design)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rotateKey]);

  // Separate effect for location handling with safeguards
  useEffect(() => {
    // Skip if no chatRoomId
    if (!chatRoomId) return;

    // Skip if we've already processed this chatRoomId to prevent duplicate updates
    if (processedChatRoomIds.current.has(chatRoomId)) {
      console.log('QRScreen: Already processed location for this chatRoomId:', chatRoomId);
      return;
    }

    // Mark this chatRoomId as processed
    processedChatRoomIds.current.add(chatRoomId);
    console.log('QRScreen: Processing location for chatRoomId:', chatRoomId);

    // Fetch location early (for speed) but DON'T save to localStorage yet.
    // Location is stored in refs and only written after connection is confirmed
    // in handleAnonDetected(). This prevents phantom "Add name" entries.
    const fetchLocation = async () => {
      try {
        const hasPermission = await hasLocationPermission();
        console.log('QRScreen: Checking stored location permission:', hasPermission);

        if (hasPermission) {
          console.log('QRScreen: We have stored permission, fetching location (no save)');

          // One GPS call → feeds both proximity detection and venue lookup
          beginNearbyTiming(); // T0 for a nearby connect's timing record
          const gpsFix = getCurrentLocation();
          coordsPromiseRef.current = gpsFix.catch(() => null);
          locationPromiseRef.current = gpsFix
            .then((coords) => {
              console.log('[QR_SCREEN] GPS coords ready');
              noteNearby('gps_ready');
              setProximityCoords(coords);
              keepLocation(coords);
              return getLocationWithVenue(coords);
            })
            .then((data) => {
              locationDataRef.current = data;
              console.log('[QR_SCREEN] Full location (GPS + venue) stored in ref');
              return data;
            })
            .catch((err) => {
              // This promise is detached — the outer catch never sees it, so a
              // failure here was invisible AND left a stale 'granted' flag: the
              // host silently lost venue capture with the primer quiet (device
              // pass 2, Tom exchange: connection_location all-null). Log every
              // miss; on code-1 (native permission revoked, e.g. iOS "Allow
              // Once" expiring) reset the flag so the primer can re-ask.
              console.error('[QR_SCREEN] Location fetch failed:', err);
              logConnectionError('host_location_failed', {
                chatRoomId,
                code: err?.code ?? null,
                message: err?.message || 'location fetch failed',
              });
              if (err?.code === 1) resetLocationPermission();
              return null;
            });
        } else if (!isLocationPrimerQuiet()) {
          // No stored permission, show the prompt. Simple tier is QUIET here —
          // the People Connect tap owns the one location ask (user ruling,
          // device pass 1 2026-08-08); the QR screen never re-asks an SP. An
          // ANSWERED primer is also quiet for the rest of the session.
          console.log('QRScreen: No stored permission, showing location prompt');
          setShowLocationPrompt(true);
        }
      } catch (error) {
        console.error('QRScreen: Error fetching location:', error);

        // Check if this is a geolocation permission denied error
        // GeolocationPositionError has code: 1 for PERMISSION_DENIED
        if (error.code === 1) {
          console.log('QRScreen: Permission denied by browser, resetting stored permission');
          await resetLocationPermission();
          if (!isLocationPrimerQuiet()) {
            setShowLocationPrompt(true);
          }
        }
      }
    };

    fetchLocation();
    // Include chatRoomId but use debouncing to prevent issues
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRoomId]); // Intentionally only run when chatRoomId changes; context values are accessed within but shouldn't trigger re-runs

  // Ask the location primer on QR open while permission isn't granted. The
  // fetchLocation effect above only fires per new chatRoomId, so a quick
  // close/reopen (no new chatRoomId) used to skip the primer; keying on
  // isOpen makes it reliable. Quiet is earned by a GRANT, not by an answer
  // (fix 2026-08-20): a granted primer stays quiet for the session; a decline
  // stays quiet only for the current QR open, so reopening asks again. The
  // re-firing this guard was added for (device pass 2) is still stopped.
  useEffect(() => {
    if (!isOpen) {
      // A closed QR ends the "this open" window — reopening asks again.
      primerDeclinedThisOpenRef.current = false;
      // A room still waiting on the answer never goes live (step 16)
      locationAnswerRef.current?.(false);
      locationAnswerRef.current = null;
      return;
    }
    if (isLocationPrimerQuiet()) return;
    let cancelled = false;
    hasLocationPermission().then((granted) => {
      if (!cancelled && !granted) setShowLocationPrompt(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, setShowLocationPrompt]);

  // Every location prompt on the new code page, first or repeat, holds the room
  // closed until it's answered (step 16; Mark 2026-10-21: every visit asks
  // again while location isn't allowed). The start-up gate covers a prompt
  // raised as the room starts; a prompt that appears over a room already live
  // — a reopen that kept its room, or a denial found on the room's first
  // location read — closes that room and starts a fresh one behind the answer.
  useEffect(() => {
    // Never mid-exchange: a scan this visit hides the prompt (render below)
    if (
      !newCodePage ||
      !isOpen ||
      !showLocationPrompt ||
      awaitingLocation ||
      !sessionActive ||
      scan
    ) {
      return;
    }
    console.log('[QR_SCREEN] Location prompt over a live room — closing it until answered');
    const puid = getProfileUserId();
    if (puid) deactivateSession(puid);
    rearm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showLocationPrompt, awaitingLocation, sessionActive, scan]);

  // NOTE: WebSocket setup is now in the main initialization effect (STEP 4)
  // This allows parallel initialization of profile load, session activation, and WebSocket connection
  // The old separate WebSocket effect has been removed to avoid duplicate connections

  // Debounced cleanup when overlay closes — 2.5s delay allows quick reopen without teardown
  useEffect(() => {
    if (isOpen) {
      // Overlay opened — cancel any pending cleanup
      if (cleanupTimerRef.current) {
        console.log('[QR_SCREEN] Overlay reopened, cancelling pending cleanup');
        clearTimeout(cleanupTimerRef.current);
        cleanupTimerRef.current = null;
      }
      // Reopen inside the waiting window — the stale teardown no longer applies
      // (the rotate branch or full re-init takes over from here)
      if (staleTeardownTimerRef.current) {
        clearTimeout(staleTeardownTimerRef.current);
        staleTeardownTimerRef.current = null;
      }
      // Reset proximity for clean re-initialization on re-entry (→ useProximity hook)
      resetProximity();
      return;
    }

    // Overlay closed — start debounced cleanup
    // Only start timer if we were previously initialized
    if (!isInitializedRef.current) return;

    console.log('[QR_SCREEN] Overlay closed, starting 2.5s cleanup timer');

    // The ONE teardown body — shared by the legacy 2.5s path and the
    // sans-chat 5-min stale-window path (review 2026-08-09: the waiting
    // window's "bounded by session staleness" claim was server-side only —
    // the client's 1.5s detection poll ran forever after a settled close,
    // ~40 wasted queries/min on a phone radio).
    const teardownExchangeSession = () => {
      // Always tear down detection layers — they only serve the QR screen.
      // Without this, the DB subscription + polling survive and cause phantom
      // navigation when QR reopens and resetDetectionGuard() clears the guard.
      if (detectionCleanupRef.current) {
        detectionCleanupRef.current();
        detectionCleanupRef.current = null;
        console.log('[QR_SCREEN] Cleanup: Detection layers torn down');
      }

      // Check if we navigated to a connection page for this room
      // If so, ProfileConnectionPage owns the WebSocket channel — don't tear it down
      const roomId = chatRoomId;
      const channelInUse = isRoomStaged(roomId);

      if (channelInUse) {
        console.log(
          '[QR_SCREEN] Cleanup: Skipping WebSocket teardown — ProfileConnectionPage is using it'
        );
      } else {
        // Leave WebSocket channel (no one else is using it)
        if (wsCleanupRef.current) {
          wsCleanupRef.current();
          wsCleanupRef.current = null;
        }
        setWsConnected(false);
      }

      // Deactivate Supabase session
      const puid = localStorage.getItem('profile_user_id');
      if (puid) {
        deactivateSession(puid).catch((err) => {
          console.error('[QR_SCREEN] Error deactivating session:', err);
        });
      }
      // Proximity teardown (→ useProximity hook)
      cleanupProximity();
      // Reset readiness state for next open
      setSessionActive(false);
      setProfileSynced(false);
      // Allow re-init on next open
      isInitializedRef.current = false;
    };

    cleanupTimerRef.current = setTimeout(() => {
      // Sans-chat waiting card (Step 7a — THE structural change): the host
      // closes the QR and sits on People waiting; detection + session must
      // SURVIVE the close so the new person can materialize as the top card.
      // Detection firing while "closed" is exactly the desired outcome — it
      // navigates to People.
      if (isSansChatVariant()) {
        console.log('[QR_SCREEN] Sans-chat: keeping detection + session alive (waiting card)');
        // PXP presence does NOT survive the close (device pass 2): proximity
        // requires both QRs open — leave the channel; the reset key + retained
        // coords re-join it on reopen. Scan detection + session stay alive.
        resetProximity();
        // Final heartbeat at close: the 2-min heartbeat stops with this close,
        // so without this touch the server-side freshness bound
        // (SESSION_FRESHNESS_MS, 5 min) could expire up to 2 min into the
        // 5-min waiting window below. Touching now starts the staleness clock
        // at close, making the enforced window exactly the designed one.
        const touchPuid = getProfileUserId();
        if (touchPuid) touchSession(touchPuid).catch(() => {});
        // This close has settled — the exchange moment is over. The next
        // open rotates to a fresh room (B28); detection stays live on THIS
        // room until then so the waiting card still materializes.
        reopenRotateRef.current = true;
        cleanupTimerRef.current = null;
        // The waiting window CLOSES with the session-staleness bound: past
        // 5 minutes no scan can succeed server-side (the touchSession
        // heartbeat stopped at close), so the client stops paying for
        // detection too. Reopen cancels this; the next open re-inits fully.
        staleTeardownTimerRef.current = setTimeout(() => {
          console.log('[QR_SCREEN] Waiting window expired (5 min) — tearing down');
          teardownExchangeSession();
          staleTeardownTimerRef.current = null;
        }, STALE_WAIT_WINDOW_MS);
        return;
      }
      console.log('[QR_SCREEN] Cleanup timer fired — tearing down session');
      teardownExchangeSession();
      cleanupTimerRef.current = null;
    }, 2500);

    return () => {
      if (cleanupTimerRef.current) {
        clearTimeout(cleanupTimerRef.current);
        cleanupTimerRef.current = null;
      }
    };
  }, [isOpen, chatRoomId, resetProximity, cleanupProximity]);

  // Immediate cleanup on actual unmount (component removed from DOM)
  useEffect(() => {
    return () => {
      console.log('[QR_SCREEN] Component unmounting, immediate cleanup');
      // Cancel any pending debounced cleanup + the stale-window teardown
      if (cleanupTimerRef.current) {
        clearTimeout(cleanupTimerRef.current);
      }
      if (staleTeardownTimerRef.current) {
        clearTimeout(staleTeardownTimerRef.current);
      }
      // Immediate detection + WebSocket cleanup
      if (detectionCleanupRef.current) {
        detectionCleanupRef.current();
      }
      if (wsCleanupRef.current) {
        wsCleanupRef.current();
      }
      // Proximity teardown (→ useProximity hook)
      cleanupProximity();
      // Deactivate session
      const currentProfileUserId = localStorage.getItem('profile_user_id');
      if (currentProfileUserId) {
        deactivateSession(currentProfileUserId).catch((err) => {
          console.error('[QR_SCREEN] Error deactivating session:', err);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = only runs on actual unmount

  // Record timestamp when QR overlay opens (for stale check on navigation)
  useEffect(() => {
    if (!isOpen) return;
    sessionStorage.setItem('qrLastFullLoad', Date.now().toString());
    console.log('[QR_SCREEN] Recorded full load timestamp');
  }, [isOpen]);

  // Auto-refresh on visibility change if connection unhealthy
  useEffect(() => {
    if (!chatRoomId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[QR_SCREEN] Page became visible, checking connection');

        if (!isChannelHealthy(chatRoomId)) {
          console.log('[QR_SCREEN] Connection unhealthy, showing refresh prompt');
          setWsConnected(false);
        } else {
          console.log('[QR_SCREEN] Connection healthy');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [chatRoomId]);

  // Session keep-alive heartbeat — prevents Edge function 5-minute staleness rejection
  // Runs every 2 minutes while QR screen is open and session is active
  useEffect(() => {
    // Only run heartbeat if session is active and we have a profileUserId
    if (!isOpen || !sessionActive || !profileUserId) return;

    console.log('[QR_SCREEN] Starting session keep-alive heartbeat (every 2 min)');

    const heartbeatInterval = setInterval(
      async () => {
        console.log('[QR_SCREEN] Heartbeat: Touching session...');
        const success = await touchSession(profileUserId);

        if (success) {
          console.log('[QR_SCREEN] Heartbeat: Session touched successfully');
        } else {
          console.warn('[QR_SCREEN] Heartbeat: Session touch failed - marking not ready');
          setSessionActive(false);
        }
      },
      2 * 60 * 1000
    ); // 2 minutes

    return () => {
      console.log('[QR_SCREEN] Stopping session keep-alive heartbeat');
      clearInterval(heartbeatInterval);
    };
  }, [isOpen, sessionActive, profileUserId]);

  // Fix 3: WebSocket channel heartbeat — detect silently dead connections (FM1)
  // Checks DB channel state every 15 seconds. Previously sent broadcast pings on the
  // main chat channel, but that channel carried zero messages — the ping tested the
  // wrong channel. Now checks the DB channel directly (the actual delivery channel).
  // Supabase's transport-level auto-reconnect handles WebSocket drops (backoff 1s/2s/5s/10s).
  useEffect(() => {
    if (!chatRoomId || !wsConnected || !isOpen) return;

    const heartbeat = setInterval(() => {
      if (!isChannelHealthy(chatRoomId)) {
        console.warn('[QR_SCREEN] Channel heartbeat: DB channel unhealthy');
        setWsConnected(false);
      }
    }, 15000); // Every 15 seconds

    return () => clearInterval(heartbeat);
  }, [chatRoomId, wsConnected, isOpen]);

  // ========== PXP SCAN HANDLER ==========
  // Quick add button handler removed — function moved to ProfileConnectionsListPage
  // The QuickAddOverlay and handleQuickAddSave still exist here for the overlay UI

  // Handle overlay Save — persists name, note, metadata, then navigates to detail page
  const handleQuickAddSave = async ({ name, note }) => {
    const chatRoomId = quickAddChatRoomId;

    await updateManualContactData(chatRoomId, {
      name,
      isManualContact: true,
    });

    if (note) {
      await updateNotes(chatRoomId, note, true);
    }

    if (quickAddMetadata) {
      await updateConnectionMetadata(chatRoomId, quickAddMetadata);
    }

    // Analytics: log manual add + location backfill
    logManualAdd(chatRoomId, name, !!note, 'Other');
    if (quickAddMetadata?.locationData) {
      logConnectionLocation(chatRoomId, quickAddMetadata.locationData, 'quick_add');
    }

    updateSessionChatRoomId(chatRoomId);
    navigateTo('manual-add-detail', { chatRoomId, source: 'quick-add' });

    setShowQuickAdd(false);
    setQuickAddChatRoomId(null);
    setQuickAddMetadata(null);
  };

  // Handle overlay dismiss (backdrop tap) — nothing saved, no connection created
  const handleQuickAddClose = () => {
    setShowQuickAdd(false);
    setQuickAddChatRoomId(null);
    setQuickAddMetadata(null);
  };

  // PXP Guest scan — thin wrapper passes config to qrPXPExchange.js module
  // `trigger` = how it started (in-app camera by default; Proximity and an
  // opened link pass their own) — rides the handshake into the host's event
  const handleScanSuccess = (decodedText, trigger = 'in_app_scan') => {
    const run = handlePXPGuestScan(decodedText, {
      profileUserId,
      profileRef,
      locationDataRef,
      locationPromiseRef,
      updateAnonData,
      updateConnectionMetadata,
      updateSessionChatRoomId,
      navigateTo,
      onClose: onCloseRef.current,
      setScanMessage,
      setShowScanner,
      trigger,
      newLanding: isNewExperience(),
      // The new code page (E3b-2a): the offer goes to this phone's own code page
      onOffer: newCodePage
        ? (o) => {
            setShowScanner(false);
            setScanMessage(null);
            setOffer(o);
          }
        : undefined,
      onBusy: newCodePage ? (name) => setOfferFailed({ reason: 'busy', name }) : undefined,
    }).catch(() => {
      // Detection or exchange failed via QR scan path — show error on scanner
      showScanLine(setScanMessage, { type: 'error', text: COULD_NOT_CONNECT });
      if (newCodePage && trigger === 'proximity') setOfferFailed({ reason: 'failed' });
    });
    // The new code page's camera stays open while this runs (9a): it reads nothing new until it settles
    return newCodePage && trigger === 'in_app_scan' ? run : undefined;
  };
  // Bridge for useProximity's PXP bridge (defined before handleScanSuccess in render order)
  scanSuccessRef.current = handleScanSuccess;

  if (isLoading) {
    return (
      <div
        style={{
          height: '100vh',
          width: '100%',
          backgroundColor: COLORS.WHITE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderTopColor: '#4a4a4a',
            borderRadius: '50%',
            animation: 'qr-spin 1s linear infinite',
          }}
        />
        <style>{`
          @keyframes qr-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <Container
        title="Error"
        onBack={handleViewConnections}
        backgroundColor={COLORS.WHITE}
        style={{ height: '100vh' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING.MD,
            height: '100%',
          }}
        >
          <div
            style={{
              color: COLORS.ERROR,
              marginBottom: SPACING.MD,
              ...TYPOGRAPHY.BODY_MEDIUM,
            }}
          >
            {error}
          </div>
          <ButtonRounded text="Try Again" onClick={handleViewConnections} />
        </div>
      </Container>
    );
  }

  // The in-app scanner, mounted by both designs (the old QR screen; the new code page's Scan circle)
  const scannerEl = isOpen && showScanner && (
    <React.Suspense fallback={null}>
      <QRScanner
        onClose={() => {
          setShowScanner(false);
          setScanMessage(null);
        }}
        onScanSuccess={handleScanSuccess}
        scanMessage={scanMessage}
      />
    </React.Suspense>
  );

  // The new code page (E3a Step 9): the page, plus the location ask on its
  // rail — every exchange until allowed (the isOpen effect above), titled
  // `When connecting` (5A.8). Leaving — the caret, or after Share — lands on
  // new People, with the person just met on top. On a test site only, the old
  // screen's row under the code: status, build number, the code's link (Mark,
  // 2026-10-21) — never on main, where the old screen's warm-up pill also stays off.
  if (newCodePage) {
    const leaveToPeople = (room) => {
      navigateTo('new-people', room ? { arrivedRoom: room } : {}, true);
      onClose?.();
    };
    return (
      <>
        <GuestCodePage
          profileUserId={profileUserId}
          qrCodeUrl={qrCodeUrl}
          profile={profile}
          scan={scan}
          onDone={leaveToPeople}
          open={isOpen}
          onRearm={rearm}
          awaitingLocation={awaitingLocation || (isOpen && showLocationPrompt && !scan)}
          offer={offer}
          offerFailed={offerFailed}
          nearby={{
            user: nearbyUser,
            waitingOn: nearbyWaitingOn,
            matched: proximityState === 'connecting',
            invite: inviteNearby,
            withdraw: withdrawNearby,
          }}
          onScan={() => setShowScanner(true)}
          readout={
            isBranchDeploy() ? (
              <CodeStatusRow
                ready={isFullyReady}
                stuck={connectionStuck}
                showDev
                profileUserId={profileUserId}
                onEditId={() => setShowEditProfileId(true)}
              />
            ) : null
          }
        />
        <AnimatePresence>
          {isOpen && showLocationPrompt && !scan && (
            <LocationPrompt
              context="code"
              onComplete={(allowed, error, coords) => {
                if (allowed === true) markLocationPrimerAnswered();
                else primerDeclinedThisOpenRef.current = true;
                handleLocationPromptComplete(allowed, error, coords);
                // Answered either way: the room can go live now (step 16)
                locationAnswerRef.current?.(true);
                locationAnswerRef.current = null;
              }}
            />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showBrowserPermissionModal && (
            <BrowserPermissionModal onClose={handleDismissPermissionModal} />
          )}
        </AnimatePresence>
        {showEditProfileId && (
          <EditProfileIdModal
            initialId={profileUserId}
            onClose={() => setShowEditProfileId(false)}
          />
        )}
        {/* The Scan circle's in-app scanner (D14) */}
        {scannerEl}
      </>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: COLORS.WHITE,
        overflow: 'hidden',
        overscrollBehavior: 'none',
      }}
    >
      {/* ROYALE title - fixed near top */}
      <div
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top) + 48px)',
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '18px',
          fontWeight: '900',
          letterSpacing: '4px',
          color: '#000000',
        }}
      >
        ROYALE
      </div>

      {/* QR Code - positioned slightly above center (40% from top) */}
      <div
        style={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className="qr-code-wrapper" style={{ position: 'relative' }}>
          {qrCodeUrl && !error && (
            <CustomQRDisplay
              profileUserId={profileUserId}
              fallbackUrl={qrCodeUrl}
              photoUrl={profile?.photoUrl}
              photoMeta={profile?.photoMeta}
              size={300}
            />
          )}
        </div>

        <CodeStatusRow
          ready={isFullyReady}
          stuck={connectionStuck}
          showDev={isBranchDeploy()}
          profileUserId={profileUserId}
          onEditId={() => setShowEditProfileId(true)}
        />

        {/* Simple tier (6c/7c): orientation caption — anchored INSIDE the QR
            block, below the status/dev row's band (it sat at a fixed screen
            offset before and printed straight over that row — device pass 2).
            First run: "Show this to swap contacts"; once the Make-QR flow is
            finished: the shorter "Swap contacts" (7c). */}
        {isSimpleTier && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: '64px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '16px',
              color: '#000000',
              whiteSpace: 'nowrap',
            }}
          >
            <Lightning size={18} weight="regular" />
            Show this to swap contacts
          </div>
        )}
      </div>

      {/* Bottom button — Simple tier gets the standard down caret, same as the
          return QR. The [Add your socials]/Later invitation that used to sit
          here is GONE (user ruling 2026-08-16): by this screen they already
          have a name and a contact, so they can connect — asking for socials
          is one ask too many at the moment the QR is the point. Full keeps its
          connections caret. */}
      {isSimpleTier ? (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(56px + env(safe-area-inset-bottom))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
          }}
        >
          <div style={{ filter: 'drop-shadow(0 2px 6px rgba(0, 0, 0, 0.12))' }}>
            <IconCircleButton
              icon={CaretDown}
              iconWeight="thin"
              size={28}
              buttonSize={56}
              iconColor="#BBBBBB"
              onClick={() => onClose?.()}
            />
          </div>
        </div>
      ) : (
        <div
          style={{
            position: 'fixed',
            bottom: pwaMode ? 'calc(82px + env(safe-area-inset-bottom))' : '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <div style={{ filter: 'drop-shadow(0 2px 6px rgba(0, 0, 0, 0.12))' }}>
            <IconCircleButton
              icon={CaretDown}
              iconWeight="thin"
              size={28}
              buttonSize={56}
              iconColor="#BBBBBB"
              onClick={handleViewConnections}
            />
          </div>
        </div>
      )}

      {/* Simple tier: photo sheet over the instant gradient QR — photo optional,
          never blocks being connectable. Raised after a beat (user ruling
          2026-08-08): let them SEE the full page first so the photo pitch is an
          invitation, not a gate. This is the ONLY sheet between "Make QR" and a
          working QR (user ruling 2026-08-16 — the People page interposes
          nothing). */}
      {isSimpleTier && (
        <QrPhotoSheet
          show={photoSheetPending && !!qrCodeUrl && photoSheetBeat}
          onPhotoSelected={async (photoUrl) => {
            const current = (await getProfileData()) || profile || {};
            const updated = { ...current, photoUrl };
            await saveProfileData(updated);
            setProfile(updated);
            syncProfileToSupabase({
              profileUserId: updated.profileUserId,
              name: updated.name,
              photoUrl,
            });
            dismissPhotoSheet();
          }}
          onDismiss={dismissPhotoSheet}
        />
      )}

      {/* Non-PWA: subtle scan icon in bottom-left corner for PXP testing */}
      {!pwaMode && (
        <div
          onClick={() => setShowScanner(true)}
          style={{
            position: 'fixed',
            bottom: 'calc(12px + env(safe-area-inset-bottom))',
            left: 12,
            zIndex: 100,
            opacity: 0.35,
            cursor: 'pointer',
          }}
        >
          <Scan size={20} color="#8E8E93" weight="light" />
        </div>
      )}

      {/* PWA URL Bar - scan icon in pill, no plus */}
      {pwaMode && (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(2px + env(safe-area-inset-bottom))',
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          {/* URL pill with scan icon */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0',
              padding: '14px 20px',
              backgroundColor: '#FFFFFF',
              borderRadius: '28px',
              boxShadow: SHADOWS.TIER1,
            }}
          >
            <div
              onClick={() => setShowScanner(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '20px',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Scan size={20} color="#8E8E93" weight="light" />
            </div>
            <span
              style={{
                fontSize: '20px',
                fontWeight: '400',
                color: '#000000',
                fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                letterSpacing: '-0.2px',
                padding: '0 12px',
              }}
            >
              royale.app
            </span>
            <div
              onClick={() => window.location.reload()}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '20px',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <ArrowClockwise size={20} color="#8E8E93" weight="regular" />
            </div>
          </div>
        </div>
      )}

      {/* Proximity Card — nearby user detection */}
      <AnimatePresence>
        {proximityState !== 'idle' && nearbyUser && (
          <ProximityCard
            state={proximityState}
            nearbyUser={nearbyUser}
            onConnect={handleProximityConnect}
            onDismiss={handleProximityDismiss}
          />
        )}
      </AnimatePresence>

      {/* Quick Add Overlay */}
      <AnimatePresence>
        {showQuickAdd && quickAddChatRoomId && (
          <QuickAddOverlay onSave={handleQuickAddSave} onClose={handleQuickAddClose} />
        )}
      </AnimatePresence>

      {/* Location prompt — slide-up bottom sheet (the map-pin one; the other
          Location surface, BrowserPermissionModal, is the terminal
          "fix it in Settings" notice for a hard denial, not a second ask).
          A GRANT marks the primer answered for the session; a decline is
          scoped to this QR open — see isLocationPrimerQuiet above. isOpen gate: the chatRoomId-keyed
          trigger can fire while the overlay is hidden, and an invisible
          prompt would still lock body scroll under the People page.
          photoSheetPending gate: the photo sheet asks first and they share the
          bottom of the screen — this one raises the moment that is answered. */}
      <AnimatePresence>
        {isOpen && showLocationPrompt && !photoSheetPending && (
          <LocationPrompt
            context="qr"
            onComplete={(allowed, error, coords) => {
              // Only a GRANT earns silence for the session. A dismissal or a
              // denial silences this QR open only — see the helper docblock.
              if (allowed === true) markLocationPrimerAnswered();
              else primerDeclinedThisOpenRef.current = true;
              handleLocationPromptComplete(allowed, error, coords);
            }}
          />
        )}
      </AnimatePresence>

      {/* Browser permission modal — terminal (single dismiss) */}
      <AnimatePresence>
        {showBrowserPermissionModal && (
          <BrowserPermissionModal onClose={handleDismissPermissionModal} />
        )}
      </AnimatePresence>

      {/* Contact Selection: Now handled automatically on ProfileConnectionPage */}

      {/* QR Scanner - camera viewfinder for PXP connections */}
      {scannerEl}

      {showEditProfileId && (
        <EditProfileIdModal initialId={profileUserId} onClose={() => setShowEditProfileId(false)} />
      )}
    </div>
  );
};

export default QRScreen;
